/* eslint-disable @typescript-eslint/no-empty-object-type */
import { CurrencyCodes, Maybe } from '@monetize/types/common';
import z from 'zod';
import { AddressSchema } from './address.types';
import { IBillGroupResp } from './billGroup.types';
import { ContractStatusEnum } from './contract.types';
import {
  ProductTypeEnum,
  QuoteStatusEnum,
  QuoteTypeEnum,
  RateBillingFrequencyEnum,
} from './enums.types';
import { IInvoiceRespSchema } from './invoice.types';
import { PaymentStatusEnum } from './payment.types';
import { PaymentGatewayTypeEnum } from './paymentGateway.types';
import {
  IPaymentMethodResp,
  PaymentMethodTypeEnum,
} from './paymentMethod.types';
import { SubscriptionTimingEnum } from './rate.types';
import { ApplyRecordAutomationRulesResponse } from './rules.types';

export interface CustomerPortalConfigurationErrorContext {
  errorKey: string;
  details: string;
  type: CustomerPortalAccessRuleTypeEnum;
  value: string;
}

export enum CustomerPortalAccessRuleTypeEnum {
  EMAIL = 'EMAIL',
  DOMAIN = 'DOMAIN',
}

export const CustomerPortalAccountConfigurationAccessRuleSchema = z.object({
  type: z.enum(CustomerPortalAccessRuleTypeEnum),
  value: z.string().toLowerCase().trim(),
});

export type CustomerPortalAccountConfigurationAccessRule = z.infer<
  typeof CustomerPortalAccountConfigurationAccessRuleSchema
>;

export const CustomerPortalAccountConfigurationRequestSchema = z.object({
  accessRules: z.array(CustomerPortalAccountConfigurationAccessRuleSchema),
});

export type CustomerPortalAccountConfigurationRequest = z.infer<
  typeof CustomerPortalAccountConfigurationRequestSchema
>;

export const CustomerPortalAccountConfigurationFormSchema = z.object({
  emailAllowlist: z
    .object({
      id: z.number(),
      value: z.union([z.literal(''), z.email().trim()]),
    })
    .array()
    .transform((val) => val.filter((item) => item.value)),
  domainAllowlist: z
    .object({
      id: z.number(),
      value: z.union([
        z.literal(''),
        z.string().regex(z.regexes.domain, {
          message: 'Invalid domain format',
        }),
      ]),
    })
    .array()
    .transform((val) => val.filter((item) => item.value)),
});

export type CustomerPortalAccountConfigurationForm = z.infer<
  typeof CustomerPortalAccountConfigurationFormSchema
>;

export const CustomerPortalAccountConfigurationResponseSchema = z.object({
  billGroupId: z.string(),
  emailAllowlist: z.array(z.string()),
  domainAllowlist: z.array(z.string()),
});

export type CustomerPortalAccountConfigurationResponse = z.infer<
  typeof CustomerPortalAccountConfigurationResponseSchema
>;

export interface PreviewCustomerPortalSessionRequest {
  accountId: string;
  billGroupId: string;
}

export interface PreviewCustomerPortalSessionResponse {
  billingPortalSession: { id: string; expiresAt: string };
}

export interface GenerateCustomerPortalShareLinkRequest {
  accountId: string;
  billGroupId: string;
}

export interface GenerateCustomerPortalShareLinkResponse {
  url: string;
}

export interface CustomerPortalSessionData {
  tenantId: string;
  accountId: string;
  billGroupId: string;
  sessionId: string;
  accessToken: string;
  email: string;
}

export interface CustomerPortalSessionInitRequest {
  tenantId: string;
  shareSecret: string;
  email: string;
}

export interface CustomerPortalSessionInitResponse {
  id: string;
  minutesUntilChallengeExpiration: number;
}

export interface CustomerPortalSessionActivateRequest {
  tenantId: string;
  billingPortalSessionId: string;
  challengeCode: string;
}

export interface CustomerPortalSessionActivateResponse {
  accessToken: string;
  accountId: string;
  billGroupId: string;
  billingPortalSession: {
    id: string;
    expiresAt: string;
  };
}

export type CustomerPortalRuleKeys =
  | 'allowPayment'
  | 'billingSummary'
  | 'contacts'
  | 'defaultPaymentMethod'
  | 'invoices'
  | 'newPaymentMethod'
  | 'paymentMethods'
  | 'purchaseOrderNumber'
  | 'registrationNumber'
  | 'subscriptions'
  | 'vatNumber';

export interface CustomerPortalRawData {
  account: {
    id: string;
    name: string;
  };
  billGroup: CustomerPortalBillGroup;
  rules: Maybe<ApplyRecordAutomationRulesResponse>;
  supportConfiguration: {
    supportEmail: Maybe<string>;
  };
}

export interface CustomerPortalData extends CustomerPortalRawData {
  permissions: {
    billingSummary: {
      view: boolean;
      edit: boolean;
      purchaseOrderNumber: CustomerPortalActionViewEdit;
      vatNumber: CustomerPortalActionViewEdit;
      registrationNumber: CustomerPortalActionViewEdit;
    };
    contacts: CustomerPortalActionViewEdit;
    invoices: {
      view: boolean;
      pay: boolean;
    };
    paymentMethods: {
      view: boolean;
      create: boolean;
      changeDefault: CustomerPortalActionViewEdit;
    };
    subscriptions: {
      view: boolean;
    };
  };
}

interface CustomerPortalActionViewEdit {
  view: boolean;
  edit: boolean;
}

export interface CustomerPortalCompanyInfoResponse {
  id: Maybe<string>;
  companyName: Maybe<string>;
  companyLogo: Maybe<string>;
}

export interface CustomerPortalPaymentMethodTokenRequest {
  paymentGatewayId: string;
  /**
   * If provided, the amount will be used
   * to determine which payment methods the customer can add
   * based on the invoice amount and the payment gateway's supported payment method types and limits.
   */
  invoiceId?: string;
}

export interface CustomerPortalPaymentMethodTokenResponse {
  paymentGateway: {
    id: string;
    type: PaymentGatewayTypeEnum;
    clientSecret: string;
    publicKey: string;
  };
}

export interface CustomerPortalPaymentMethodCreateRequest {
  paymentGatewayId: string;
  paymentMethod: {
    paymentToken: string;
    paymentMethodName: string;
    amount: number;
    description?: string;
    setAsDefault?: boolean;
    billingDetails: {
      fullName: string;
      email?: string;
      phone?: string;
      title?: string;
      line1?: string;
      line2?: string;
      city?: string;
      state?: string;
      postalCode?: string;
      country?: string;
    };
  };
}

export type CustomerPortalPaymentMethodCreateResponse =
  CustomerPortalPaymentMethod;

export interface CustomerPortalPaymentRequest {
  invoiceId: string;
  paymentMethodId: string;
  amount: number;
}

export interface CustomerPortalPaymentResponse {
  paymentId: string;
  paymentDate: string;
  amount: number;
  appliedAmount: number;
  unappliedAmount: number;
  fullyApplied: boolean;
  currency: string;
  status: PaymentStatusEnum;
  paymentType: PaymentMethodTypeEnum;
  paymentMethodId?: string;
}

export interface CustomerPortalBillGroupUpdateRequest {
  id: string;
  purchaseOrderNumber?: string | null;
  vatNumber?: string | null;
  registrationNumber?: string | null;
  paymentMethodId?: string | null;
}

export interface CustomerPortalBillGroupUpdateResponse {
  billGroupId: string;
  paymentMethodId: Maybe<string>;
  purchaseOrderNumber: Maybe<string>;
  vatNumber: Maybe<string>;
  registrationNumber: Maybe<string>;
}

export interface CustomerPortalContact {
  id: string;
  fullName: string;
  email: string;
  phone: string;
}

export interface CustomerPortalBillGroup extends Pick<
  IBillGroupResp,
  | 'id'
  | 'accountId'
  | 'status'
  | 'nextInvoiceDate'
  | 'invoicingFrequency'
  | 'invoicingFrequencyInMonths'
  | 'collectInvoiceBalanceAutomatically'
  | 'paymentMethodId'
  | 'name'
  | 'netTerms'
  | 'purchaseOrderNumber'
  | 'currency'
  | 'vatNumber'
  | 'registrationNumber'
  | 'dunningStatus'
  | 'addressSource'
  | 'billingAddress'
  | 'shippingAddress'
> {
  billingContact: CustomerPortalContact;
  shippingContact: CustomerPortalContact;
  latestInServiceContract: {
    id: string;
    status: ContractStatusEnum;
    latestQuoteId: string | null;
    term: {
      start: string;
      end: string;
      lengthInDays: number;
    };
  };
  defaultPaymentGatewayId?: string;
}

export interface CustomerPortalInvoice extends Pick<
  IInvoiceRespSchema,
  | 'id'
  | 'invoicingDate'
  | 'billDate'
  | 'accountingDate'
  | 'dueDate'
  | 'lastSent'
  | 'status'
  | 'amount'
  | 'currency'
  | 'startDate'
  | 'endDate'
  | 'amountDue'
  | 'amountPaid'
  | 'paidDate'
  | 'collectionAttempts'
  | 'amountCredited'
  | 'pastDue'
  | 'usageStartDate'
  | 'usageEndDate'
  | 'purchaseOrderNumber'
  | 'quoteId'
  | 'contractId'
  | 'netTerms'
  | 'billGroupId'
  | 'taxStatus'
  | 'taxError'
  | 'vatNumber'
  | 'registrationNumber'
  | 'invoiceNumber'
> {}

export interface CustomerPortalPaymentMethod extends Pick<
  IPaymentMethodResp,
  | 'id'
  | 'paymentMethodName'
  | 'defaultPaymentMethod'
  | 'creditCardType'
  | 'creditCardNumber'
  | 'lastFour'
  | 'expirationYear'
  | 'expirationMonth'
  | 'paymentMethodType'
  | 'description'
  | 'status'
  | 'externalPaymentMethodId'
  | 'accountType'
  | 'bankName'
  | 'routingNumber'
  | 'swiftCode'
  | 'accountNumber'
  | 'billingDetails'
  | 'cancellationDate'
  | 'cancellationReason'
> {
  paymentGateway: { id: string; type: PaymentGatewayTypeEnum };
}

export interface SubscriptionViewItem {
  quoteItemId: string;
  productId: string;
  productName: Maybe<string>;
  productType: ProductTypeEnum;
  quantity: number;
  unitPrice: number;
  unitPriceAfterDiscount: number;
  displayUnitPrice: Maybe<number>;
  displayUnitPriceAfterDiscount: Maybe<number>;
  displayUnitPriceFrequency: Maybe<number>;
  isVariableUnitPrice: boolean;
  discountAmount: number;
  discountPercent: number;
  amount: number;
  mrr: number;
  arr: number;
}

export interface SubscriptionViewOfferingSegment {
  quoteOfferingId: string;
  parentQuoteOfferingId: Maybe<string>;
  offeringId: string;
  offeringName: Maybe<string>;
  rateId: string;
  rateName: Maybe<string>;
  billingFrequency: Maybe<RateBillingFrequencyEnum>;
  billingFrequencyInMonths: Maybe<number>;
  subscriptionTiming: Maybe<SubscriptionTimingEnum>;
  startDate: string;
  endDate: string;
  mrr: number;
  arr: number;
  amount: number;
  items: SubscriptionViewItem[];
}

export interface SubscriptionViewOffering {
  position: number;
  hasRamps: boolean;
  isRemoved: boolean;
  activeSegment: SubscriptionViewOfferingSegment;
  allSegments: SubscriptionViewOfferingSegment[];
}

export interface SubscriptionView {
  quoteId: string;
  quoteStatus: QuoteStatusEnum;
  quoteType: QuoteTypeEnum;
  description: Maybe<string>;
  billGroupId: Maybe<string>;
  contractId: Maybe<string>;
  processedAt: Maybe<string>;
  modifyDate: string;
  currency: CurrencyCodes;
  contractStartDate: Maybe<string>;
  contractEndDate: Maybe<string>;
  contractLength: Maybe<number>;
  mrr: number;
  arr: number;
  amount: number;
  offerings: SubscriptionViewOffering[];
}

export const CustomerPortalContactUpdateSchema = z.object({
  id: z.string().nullish(),
  fullName: z.string(),
  email: z.email(),
  phone: z.string().nullish(),
  title: z.string().nullish(),
  address: AddressSchema.nullish(),
});
export type CustomerPortalContactUpdate = z.infer<
  typeof CustomerPortalContactUpdateSchema
>;

export const CustomerPortalContactUpdateRequestSchema = z.object({
  billingContact: CustomerPortalContactUpdateSchema,
  shippingContact: CustomerPortalContactUpdateSchema,
  useSameShippingBilling: z.boolean(),
});
export type CustomerPortalContactUpdateRequest = z.infer<
  typeof CustomerPortalContactUpdateRequestSchema
>;

export type CustomerPortalContactUpdateResponse = Pick<
  CustomerPortalBillGroup,
  'billingAddress' | 'billingContact' | 'shippingAddress' | 'shippingContact'
>;

// TODO:
export interface CustomerPortalPayment {}
